import { useState, type ReactNode } from "react";
import { ChevronDown } from "lucide-react";

interface SubmissionHistorySectionProps<T extends { id: string }> {
  submissions: T[];
  isLoading: boolean;
  error: string;
  renderSubmission: (submission: T, position: number) => ReactNode;
  containerClassName?: string;
  onExpandedChange?: (expanded: boolean) => void;
}

const SubmissionHistorySection = <T extends { id: string }>({
  submissions,
  isLoading,
  error,
  renderSubmission,
  onExpandedChange,
}: SubmissionHistorySectionProps<T>) => {
  const [expanded, setExpanded] = useState(false);

  const toggle = () => {
    const nextExpanded = !expanded;
    setExpanded(nextExpanded);
    onExpandedChange?.(nextExpanded);
  };

  return (
    <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 text-sm p-2">
      <button
        type="button"
        onClick={toggle}
        className="flex w-full items-center justify-between text-left font-bold text-blue-800 cursor-pointer"
      >
        <span>Submission History</span>
        <ChevronDown
          className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        <div className="mt-3 space-y-3 text-blue-900">
          {isLoading && submissions.length === 0 && (
            <p>Loading submission history...</p>
          )}
          {isLoading && submissions.length > 0 && (
            <p className="text-xs text-blue-600">Refreshing...</p>
          )}
          {error ? (
            <p>{error}</p>
          ) : !isLoading && submissions.length === 0 ? (
            <p>No submission history found.</p>
          ) : (
            submissions.map(renderSubmission)
          )}
        </div>
      )}
    </div>
  );
};

export default SubmissionHistorySection;
